import React from "react";
import "./generic-error.scss";

interface IGenericErrorProps {
  message?: Element | string;
  nextAction?: () => void;
  nextActionLabel?: Element | string;
  details?: Element | string;
}

/**
 * The GenericError is intended for rare cases when React is unable to render due to uncaught errors in the UI application. It is designed to have the minimum number of dependencies to avoid having errors inside GenericError.
 * If you need to trigger this error, add the following anywhere in the render chain, for example in the constructor of `home-container.tsx`.
 *    // Trigger an unhandled error to test
      const thisThing: any = {};
      // eslint-disable-next-line dot-notation
      thisThing["will create"]["a CUSTOM ERROR"] = "by triggering a null reference";
 * @param props Requires an error `message`.
 * Can take a `nextAction` like navigating to another page, clearing cookies, etc. By default `nextAction` will reload the page.
 * Can take a `nextActionLabel` that is displayed on the button.
 * @returns An instance of Generic Error which is a React component.
 */
function GenericError(props: IGenericErrorProps) {
  const { message, nextAction, nextActionLabel, details } = props;
  return (
    <div className="generic-error-msg">
      <div className="generic-error-msg-content">
        <div className="generic-error-msg-title">{message ?? "Sorry, something went wrong!"}</div>
        <div className="generic-error-msg-details">{details}</div>
        <div className="generic-error-msg-button">
          <button type="button" className="btn" onClick={nextAction ?? (() => window.location.reload())}>
            {nextActionLabel ?? "Reload"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GenericError;
